// ────────────────────────────────────────────────────────────────
//  PrintModal.jsx   – author + note + PICK-YOUR-SECTIONS
// ────────────────────────────────────────────────────────────────
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiPrinter, FiCheckSquare, FiSquare } from 'react-icons/fi';
import { useProjects } from '../context/ProjectContext';
import AutoTextarea from './AutoTextarea';
import { downloadPdfReport } from '../utils/printReport';
import { get as idbGet, set as idbSet } from '../utils/idbKV';

/* helper ------------------------------------------------------------------ */
const Checkbox = ({ checked, onChange, label, style = {} }) => (
    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', ...style }}>
        {checked ? <FiCheckSquare /> : <FiSquare />}
        <input
            type="checkbox"
            checked={checked}
            onChange={e => onChange(e.target.checked)}
            style={{ display: 'none' }}
        />
        <span style={{ marginLeft: 6 }}>{label}</span>
    </label>
);

export default function PrintModal({ open, onClose }) {
    const { activeProject, setReportNote } = useProjects();

    /* ───────── A)  Author + note ───────── */
    const [author, setAuthor] = useState('');
    useEffect(() => {
        let alive = true;
        (async () => {
            const v = await idbGet('energiberegner:author');
            if (alive && typeof v === 'string') setAuthor(v);
        })();
        return () => { alive = false; };
    }, []);
    const [note, setNote] = useState(() => activeProject?.reportNote || '');

    /* ───────── B)  Section toggles ───────── */
    const [incUvalue, setIncUvalue] = useState(true);
    const [incVarmetab, setIncVarmetab] = useState(true);
    const [incSavings, setIncSavings] = useState(true);

    /* ── U-value sub-selection ───────────────────────── */
    const constructions = activeProject?.uvalue ?? [];
    const [uvAll, setUvAll] = useState(true);
    const [uvSel, setUvSel] = useState(() => new Set(constructions.map(c => c.id)));

    /* ── Savings sub-selection ───────────────────────── */
    const savingsRoot = activeProject?.savingsPayload ?? activeProject?.savings;
    const savedSingles = savingsRoot?.singleTests ?? [];
    const savedBuildings = savingsRoot?.buildingTests ?? [];
    const storedScenarios = [...savedSingles, ...savedBuildings];
    const scenList = storedScenarios.length
        ? storedScenarios
        : (savingsRoot && (savingsRoot.kWhSaved != null || savingsRoot.annualCost != null || savingsRoot.annualUnits != null)
            ? [savingsRoot]
            : []); // robust flat list
    const [svAll, setSvAll] = useState(true);
    const [svSel, setSvSel] = useState(() => new Set(scenList.map(s => s.id)));

    /* keep sets in sync if project changes --------------------------- */
    useEffect(() => {
        setUvSel(new Set(constructions.map(c => c.id)));
        setSvSel(new Set([...savedSingles, ...savedBuildings].map(s => s.id)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeProject?.name]);

    /* ───────── C)  Close on outside-click ───────── */
    const boxRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        const h = e => { if (boxRef.current && !boxRef.current.contains(e.target)) onClose(); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [open, onClose]);

    /* ───────── D)  Download action ───────── */
    const handleDownload = async () => {
        /* filter U-value deck */
        const uDeck = incUvalue
            ? (uvAll ? constructions : constructions.filter(c => uvSel.has(c.id)))
            : [];

        /* Varmetab */
        const vtData = incVarmetab
            ? {
                ...(activeProject.varmetabPayload?.payload ?? activeProject.varmetabPayload ?? activeProject.varmetab ?? {}),
                info: activeProject.varmetabInfo ?? activeProject.varmetabPayload?.info ?? activeProject.varmetab?.info
            }
            : null;

        /* Savings – honour user’s selection */
        let savingsData = null;
        if (incSavings && scenList.length) {
            // build a JS array of exactly the scenarios the user checked
            const chosen = svAll ? scenList : scenList.filter(s => svSel.has(s.id));
            // backward-compat: if there's exactly one, pass it as an object;
            // otherwise pass the full array.
            savingsData = chosen.length === 1 ? chosen[0] : chosen;
        }

        await downloadPdfReport({
            projectName: activeProject?.name,
            uvalueData: uDeck,
            varmetabData: vtData,
            savingsData,
            author: author.trim(),
            reportNote: note.trim()
        });

        /* persist & exit */
        await idbSet('energiberegner:author', author.trim());
        if (activeProject?.name) setReportNote(activeProject.name, note.trim());
        onClose();
    };

    /* ───────── E)  Render ───────── */
    if (typeof window === 'undefined' || !open) return null;
    return createPortal(
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5010
            }}
        >
            <div
                ref={boxRef}
                style={{
                    background: '#fff', borderRadius: 8, padding: '1.8rem 1.9rem 1.4rem',
                    width: 'min(440px,92%)', maxHeight: '90vh', overflowY: 'auto',
                    display: 'flex', flexDirection: 'column', gap: '1.1rem',
                    boxShadow: '0 8px 22px rgba(0,0,0,.15)'
                }}
            >
                <h2 style={{ margin: 0 }}>Udskriv PDF-rapport</h2>

                {/* Author + note ------------------------------------------------ */}
                <label style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                    <span>Udarbejdet af</span>
                    <input
                        className="bigSelect" type="text" placeholder="Navn / firma …"
                        value={author} onChange={e => setAuthor(e.target.value)}
                    />
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                    <span>Beskrivelse (forside)</span>
                    <div style={{ display: 'flex', gap: '.5rem' }}>
                        <AutoTextarea
                            value={note}
                            onChange={setNote}
                            placeholder="Evt. ekstra bemærkninger …"
                            className="noteArea"
                        />
                        {note && (
                            <button type="button" className="iconBtn" title="Ryd tekst"
                                onClick={() => setNote('')}>
                                ✕
                            </button>
                        )}
                    </div>
                </label>

                {/* Section selector -------------------------------------------- */}
                <div style={{ borderTop: '1px solid #ddd', paddingTop: '1rem', marginTop: '-.3rem' }}>
                    <p style={{ margin: '0 0 .5rem', fontSize: 13, color: '#555' }}>
                        Klik for at slå rapportens afsnit til eller fra.
                    </p>

                    <Checkbox checked={incUvalue} onChange={setIncUvalue} label="U-værdi" />
                    {incUvalue && (
                        <div style={{ marginLeft: 18, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <Checkbox
                                checked={uvAll}
                                onChange={v => {
                                    setUvAll(v);
                                    if (v) setUvSel(new Set(constructions.map(c => c.id)));
                                }}
                                label="Inkludér alle konstruktioner"
                            />
                            {!uvAll && constructions.map(c => (
                                <Checkbox key={c.id}
                                    checked={uvSel.has(c.id)}
                                    onChange={v => {
                                        const next = new Set(uvSel);
                                        v ? next.add(c.id) : next.delete(c.id);
                                        setUvSel(next);
                                    }}
                                    label={c.title || 'Konstruktion'}
                                    style={{ marginLeft: 18 }}
                                />
                            ))}
                        </div>
                    )}

                    <div style={{ marginTop: 10 }}>
                        <Checkbox checked={incVarmetab} onChange={setIncVarmetab} label="Varmetabs-ramme" />
                    </div>

                    <div style={{ marginTop: 10 }}>
                        <Checkbox checked={incSavings} onChange={setIncSavings} label="Årlig besparelse" />
                        {incSavings && scenList.length > 0 && (
                            <div style={{ marginLeft: 18, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <Checkbox
                                    checked={svAll}
                                    onChange={v => {
                                        setSvAll(v);
                                        if (v) setSvSel(new Set(scenList.map(s => s.id)));
                                    }}
                                    label="Inkludér alle scenarier"
                                />
                                {!svAll && scenList.map(s => (
                                    <Checkbox key={s.id}
                                        checked={svSel.has(s.id)}
                                        onChange={v => {
                                            const next = new Set(svSel);
                                            v ? next.add(s.id) : next.delete(s.id);
                                            setSvSel(next);
                                        }}
                                        label={s.name}
                                        style={{ marginLeft: 18 }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </div>


                {/* Buttons ----------------------------------------------------- */}
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
                    <button
                        className="addBtn"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        onClick={handleDownload}
                    >
                        <FiPrinter /> Download PDF
                    </button>
                    <button onClick={onClose}>Annullér</button>
                </div>
            </div>
        </div>,
        document.body
    );
}
